import { Outlet, createRootRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { useAction, useConvexAuth } from "convex/react";
import { useEffect, useState } from "react";
import { api } from "@constructionautomation/backend";

import { AppShell } from "@/components/layout";
import { WorkspaceProvider, useWorkspaceEnabled } from "@/features/workspaces";
import {
  SignInScreen,
  AuthLoadingScreen,
  OrganizationOnboardingScreen,
  UserProfileProvider,
  useUserProfile,
} from "@/components/auth";
import { ProjectProvider } from "@/components/projects";
import { Button } from "@/components/ui/button";
import { Sentry, SentryUserContext, captureUserActionError } from "@/lib/sentry";

function WorkspaceRedirect() {
  const navigate = useNavigate();
  const isWorkspacesEnabled = useWorkspaceEnabled();
  const routerState = useRouterState();

  useEffect(() => {
    if (isWorkspacesEnabled && routerState.location.pathname === "/") {
      navigate({ to: "/workspaces/new", replace: true });
    }
  }, [isWorkspacesEnabled, routerState.location.pathname, navigate]);

  return null;
}

function AuthenticatedApp() {
  const { userProfile: viewer, isPending: isViewerPending, error: viewerError } = useUserProfile();
  const syncIdentity = useAction(api.authActions.syncIdentityFromWorkos);
  const [isSyncingIdentity, setIsSyncingIdentity] = useState(false);
  const [hasAttemptedIdentitySync, setHasAttemptedIdentitySync] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  useEffect(() => {
    setHasAttemptedIdentitySync(false);
    setSyncError(null);
  }, []);

  const shouldSyncIdentity =
    viewer !== null &&
    viewer !== undefined &&
    (!viewer.isSynced || !viewer.email || !viewer.name);

  useEffect(() => {
    if (
      viewer === undefined ||
      isSyncingIdentity ||
      syncError ||
      hasAttemptedIdentitySync
    ) {
      return;
    }
    if (!shouldSyncIdentity) {
      return;
    }

    setIsSyncingIdentity(true);
    setHasAttemptedIdentitySync(true);
    void syncIdentity({})
      .then(() => {
        setSyncError(null);
      })
      .catch((error) => {
        captureUserActionError(error, "auth.syncIdentity", {
          orgId: viewer.organizationId,
        });
        setSyncError(error instanceof Error ? error.message : "Failed to sync user");
      })
      .finally(() => {
        setIsSyncingIdentity(false);
      });
  }, [
    viewer,
    isSyncingIdentity,
    syncIdentity,
    syncError,
    hasAttemptedIdentitySync,
    shouldSyncIdentity,
  ]);

  if (isViewerPending || isSyncingIdentity) {
    return <AuthLoadingScreen />;
  }

  if (viewerError) {
    throw viewerError;
  }

  if (viewer === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="w-full max-w-sm rounded-lg border bg-card p-6 text-center">
          <p className="mb-3 text-sm text-destructive">
            Your session has expired. Please sign in again.
          </p>
          <Button
            onClick={() => {
              window.location.reload();
            }}
          >
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  if (syncError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="w-full max-w-sm rounded-lg border bg-card p-6 text-center">
          <p className="mb-3 text-sm text-destructive">{syncError}</p>
          <Button
            onClick={() => {
              setSyncError(null);
              setHasAttemptedIdentitySync(false);
            }}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // After isViewerPending check, viewer should not be undefined, but TypeScript needs this guard
  if (!viewer) {
    return <AuthLoadingScreen />;
  }

  if (viewer.requiresOnboarding) {
    return <OrganizationOnboardingScreen />;
  }

  return (
    <WorkspaceProvider>
      <SentryUserContext viewer={viewer} />
      <ProjectProvider>
        <WorkspaceRedirect />
        <AppShell>
          <Outlet />
        </AppShell>
      </ProjectProvider>
      <TanStackDevtools
        config={{
          position: "bottom-right",
        }}
        plugins={[
          {
            name: "Tanstack Router",
            render: <TanStackRouterDevtoolsPanel />,
          },
        ]}
      />
    </WorkspaceProvider>
  );
}

function RootComponent() {
  const { isLoading, isAuthenticated } = useConvexAuth();
  const [showContent, setShowContent] = useState(false);

  // Delay showing content slightly after auth resolves to allow fade-in
  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => setShowContent(true), 50);
      return () => clearTimeout(timer);
    }
    setShowContent(false);
  }, [isLoading]);

  if (isLoading) {
    return <AuthLoadingScreen />;
  }

  if (!isAuthenticated) {
    return (
      <div
        className={`transition-opacity duration-300 ${showContent ? "opacity-100" : "opacity-0"}`}
      >
        <SignInScreen />
      </div>
    );
  }

  return (
    <div
      className={`h-full transition-opacity duration-300 ${showContent ? "opacity-100" : "opacity-0"}`}
    >
      <UserProfileProvider>
        <AuthenticatedApp />
      </UserProfileProvider>
    </div>
  );
}

function RootErrorComponent({ error }: { error: unknown }) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return <div className="p-6 text-sm text-destructive">Something went wrong.</div>;
}

export const Route = createRootRoute({
  component: RootComponent,
  errorComponent: RootErrorComponent,
});
